import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { DataImport } from 'graphql-common';
import { CellProps } from '@lib/components/Table/types';
import Trans from '@lib/components/Trans/Trans';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import DataImportStatusChip from '@lib/components/DataImportStatusChip/DataImportStatusChip';
import DataImportKindChip from '@lib/components/DataImportKindChip/DataImportKindChip';
import TextCellWithIcon from '@lib/components/Table/commonComponents/TextCell/TextCellWithIcon';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import isDataImportFinished from 'routes/DataImport/utils/isDataImportFinished';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';

type ColumnsArgs = {
  t: TFunction<'translation', undefined>;
  onShowDataImportErrors: (item?: DataImport) => void;
};

export default function getTableColumns({
  t,
  onShowDataImportErrors,
}: ColumnsArgs) {
  return [
    {
      id: 'attachmentFilename',
      Header: t('document-name'),
      Cell: ({ item }: CellProps<DataImport>) => (
        <DocumentationPreview attachment={item.attachmentAttached} />
      ),
      minWidth: 350,
      sortable: true,
    },
    {
      id: 'status',
      Header: t('status'),
      accessor: 'status',
      Cell: ({ item }: CellProps<DataImport>) => (
        <DataImportStatusChip data={item} />
      ),
      maxWidth: 150,
      minWidth: 150,
    },
    {
      id: 'kind',
      Header: t('type'),
      accessor: 'kind',
      Cell: ({ item }: CellProps<DataImport>) => (
        <DataImportKindChip data={item} />
      ),
      sortable: true,
      maxWidth: 130,
      minWidth: 130,
    },
    {
      id: 'counters',
      Header: t('items-imported'),
      Cell: (props: CellProps<DataImport>) => {
        const { item } = props;
        const { fileException, failureDetails, status } = item;
        if (!isDataImportFinished(status)) {
          return <TextCell {...props} value="-" />;
        }
        const totalRows = _get(item, 'totalRows') || 0;
        const importedRows = _get(item, 'importedRows') || 0;
        const hasErrors =
          failureDetails && Object.keys(failureDetails).length > 0;
        if (fileException?.length) {
          return (
            <TextCellWithIcon
              {...props}
              value={
                <Trans
                  i18nKey="count-of-total"
                  values={{ count: 0, total: 0 }}
                />
              }
              icon={
                fileException[0] ? (
                  <Tooltip
                    body={fileException[0]}
                    disabled={!failureDetails?.message}
                  >
                    <Chip
                      type={ChipTypes.chipGray}
                      leftIcon="help"
                      size={ChipSizes.small}
                      onClick={() => onShowDataImportErrors(item)}
                    />
                  </Tooltip>
                ) : undefined
              }
              iconPosition="right"
            />
          );
        }
        if (totalRows) {
          return (
            <TextCellWithIcon
              {...props}
              value={
                <Trans
                  i18nKey="count-of-total"
                  values={{ count: importedRows, total: totalRows }}
                />
              }
              icon={
                hasErrors ? (
                  <Tooltip
                    body={failureDetails?.message}
                    disabled={!failureDetails?.message}
                  >
                    <Chip
                      type={ChipTypes.chipGray}
                      leftIcon="help"
                      size={ChipSizes.small}
                      onClick={() => onShowDataImportErrors(item)}
                    />
                  </Tooltip>
                ) : undefined
              }
              iconPosition="right"
            />
          );
        }
        if (hasErrors) {
          return (
            <Tooltip
              body={failureDetails?.message}
              disabled={!failureDetails?.message}
            >
              <Chip
                type={ChipTypes.chipGray}
                leftIcon="help"
                size={ChipSizes.small}
                onClick={() => onShowDataImportErrors(item)}
              />
            </Tooltip>
          );
        }
        return <TextCell {...props} value="-" />;
      },
      maxWidth: 130,
      minWidth: 130,
    },
    {
      id: 'createdAt',
      Header: t('started-on'),
      accessorFn: ({ item }: CellProps<DataImport>) =>
        getFormattedDate(item.createdAt, dateTimeFormatEU),
      Cell: (props: CellProps<DataImport>) => <TextCell {...props} />,
      minWidth: 150,
      maxWidth: 150,
      width: 150,
      sortable: true,
    },
    {
      id: 'createdByFullName',
      Header: t('imported-by'),
      Cell: (props: CellProps<DataImport>) => {
        const { item } = props;
        const { user } = item;
        if (!user) return <TextCell {...props} value="-" />;
        return (
          <UserPreviewCell
            avatar={
              user.avatarAttached?.urls?.small ||
              user.avatarAttached?.urls?.original
            }
            name={`${user.firstName} ${user.lastName}`}
            email={user.email}
          />
        );
      },
      minWidth: 308,
      width: 308,
      sortable: true,
    },
  ];
}
